import { useEffect, useRef, useState } from 'react';
import { collabApi } from '../api/collab.js';
import { buildMentionToken } from '../lib/mentions.js';

/**
 * Minimal textarea with @mention autocomplete. Looks at the current word the
 * cursor is on; if it starts with @, fetches users via /users/search and
 * shows a dropdown. Selecting a user inserts the app-wide STRUCTURED token
 * `@[Name](user:uuid)` (see lib/mentions.js) — unambiguous, so the server never
 * has to guess a handle back into a user.
 */
export default function MentionInput({ value, onChange, placeholder, rows = 3 }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState('');
  const taRef = useRef(null);

  function update(e) {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    const m = before.match(/@([\w.-]*)$/);
    if (m) {
      setQuery(m[1]);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }

  useEffect(() => {
    if (!open || !query) { setResults([]); return; }
    const t = setTimeout(() => {
      collabApi.searchUsers(query).then((d) => { setResults(d?.items || []); setActive(0); }).catch(() => {});
    }, 120);
    return () => clearTimeout(t);
  }, [open, query]);

  function pick(user) {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const token = buildMentionToken(user.name || user.email, user.id);
    // Function replacer so `$`-sequences in a name can't be interpreted by replace().
    const before = value.slice(0, caret).replace(/@([\w.-]*)$/, () => `${token} `);
    const after = value.slice(caret);
    const newCaret = before.length;
    onChange(before + after);
    setOpen(false);
    setTimeout(() => { ta.focus(); try { ta.setSelectionRange(newCaret, newCaret); } catch { /* noop */ } }, 0);
  }

  function onKeyDown(e) {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  return (
    <div className="mention-input">
      <textarea
        ref={taRef}
        rows={rows}
        value={value}
        onChange={update}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Type @ to mention someone…'}
      />
      {open && results.length > 0 && (
        <ul className="mention-dropdown">
          {results.map((u, i) => (
            <li key={u.id} className={i === active ? 'active' : ''} onMouseDown={(e) => { e.preventDefault(); pick(u); }}>
              <span className="avatar tiny">{(u.name || u.email || '?').charAt(0).toUpperCase()}</span>
              <div>
                <div>{u.name}</div>
                <div className="muted small">{u.email}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
